import {
  Button,
  Checkbox,
  CurrencyInput,
  Drawer,
  Hairline,
  Input,
  Label,
  Loading,
  Message,
  Radio,
  Select,
  Textarea,
} from "@components";
import { useEffect, useRef, useState } from "react";
import { Add } from "shared/components/Add";
import { If } from "shared/components/control-statements";
import { Icon } from "shared/components/core/Icon";
import { SuccessIcon } from "shared/components/Icons";
import styles from "styles/steps-database.module.scss";

import * as uuid from "uuid";
import classNames from "classnames/bind";
import { AssetsAPI } from "providers/AssetsAPI";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import Toggle2 from "shared/components/Toggle2";
import { CatalogAPI } from "providers/CatalogAPI";
import { OptionStepDatabaseQuestion } from "./Product/FormSteps/Steps/OptionStepDatabaseQuestion";
import { Add2 } from "shared/components/Add2";
const css = classNames.bind(styles);

const getTotalCharacters = (product) => {
  return `${product?.sku?.length > 0 ? product?.sku?.length : 0}/60`;
};

export default function StepsDatabase({ open, setOpen, catalog }) {
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [openFinished, setOpenFinished] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [openOptionSteps, setOpenOptionSteps] = useState(false);
  const [selectedOption, setSelectedOption] = useState({});
  const [holdingId, setHoldingId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const pollingLock = useRef(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (open) {
      loadSteps();
      startPolling();
    }

    return () => stopPolling();
  }, [open]);

  const startPolling = () => {
    console.log("[POLLING] Start");

    pollingLock.current = setInterval(async () => {
      loadLockStatus();
    }, 30000);

    loadLockStatus();
  };

  const stopPolling = () => {
    if (!pollingLock.current) return;

    console.log("[POLLING] Stop");

    clearTimeout(pollingLock.current);
  };

  const loadLockStatus = async () => {
    const environmentId = EnvironmentStorage.getSelectedEnvironment();
    const lockResponse = await CatalogAPI.getCatalogLock({
      environmentId,
    });
    const lockData = lockResponse.getData({ locked: false });
    setLocked(lockData.locked);
  };

  const loadSteps = async () => {
    setLoading(true);
    const environmentId = EnvironmentStorage.getSelectedEnvironment();
    const sharedStepsResponse = await CatalogAPI.getSharedSteps(environmentId);
    const sharedSteps = sharedStepsResponse.getData([]);
    setSteps(sharedSteps);
    setLoading(false);
  };

  const upsertStep = async (step) => {
    const environmentId = EnvironmentStorage.getSelectedEnvironment();

    let response = null;

    if (step.id) {
      response = await CatalogAPI.updateSharedStep({
        environmentId,
        step,
      });
    } else {
      response = await CatalogAPI.createSharedStep({
        environmentId,
        step,
      });
    }

    if (response.getErrors([]).length > 0) {
      alert({
        text: `Não foi possível salvar a etapa - ${response.getErrors([])[0]}`,
      });
      return;
    }

    loadSteps();
    loadLockStatus();
  };

  const getEmptyQuestion = () => ({
    id: null,
    ref_id: uuid.v4(),
    name: "",
    type: "question",
    items: [],
  });

  const onChangeOption = (values) => {
    if (!values?.items) return;

    const newChildren = values.items.map((child) => ({
      ...child,
      parent: selectedOption.id,
    }));

    setSelectedQuestion((prev) => {
      if (!prev?.items) return prev;

      const updateItem = (items) =>
        items.map((item) => {
          if (item.ref_id === selectedOption.ref_id) {
            return {
              ...item,
              items: newChildren,
            };
          }

          if (item.items?.length) {
            return { ...item, items: updateItem(item.items) };
          }

          return item;
        });

      const updated = { ...prev, items: updateItem(prev.items) };

      const findUpdatedOption = (items) => {
        for (const it of items) {
          if (it.ref_id === selectedOption.ref_id) return it;
          if (it.items?.length) {
            const found = findUpdatedOption(it.items);
            if (found) return found;
          }
        }
      };

      setSelectedOption(findUpdatedOption(updated.items));

      return updated;
    });
  };

  const onDeleteQuestion = (questionIndex) => {
    setSelectedQuestion((prev) => {
      if (!prev?.items) return prev;

      const removeItem = (items) =>
        items
          .map((item) => {
            if (item.ref_id === selectedOption.ref_id) {
              const filtered =
                item.items?.filter((_, i) => i !== questionIndex) || [];
              return { ...item, items: filtered };
            }

            if (item.items?.length) {
              return { ...item, items: removeItem(item.items) };
            }

            return item;
          })
          .filter(Boolean);

      const updated = { ...prev, items: removeItem(prev.items) };

      const findUpdatedOption = (items) => {
        for (const it of items) {
          if (it.ref_id === selectedOption.ref_id) return it;
          if (it.items?.length) {
            const found = findUpdatedOption(it.items);
            if (found) return found;
          }
        }
      };

      setSelectedOption(findUpdatedOption(updated.items));

      return updated;
    });
  };

  useEffect(() => {
    const fetchHoldingId = async () => {
      const environment = await EnvironmentStorage.getEnvironment();
      setHoldingId(environment?.holdingId);
    };

    fetchHoldingId();
  }, []);

  useEffect(() => {
    if (open) setCurrentStep(null);
  }, [open]);

  const OptionItem = ({
    option,
    optionIndex,
    onChange,
    onDelete,
    handleOptionSteps,
    sortAnswers,
    moveUp,
    moveDown,
    isLast,
  }) => {
    const [useDescription, setUseDescription] = useState(option.description);
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
      if (sortAnswers) setCollapsed(false);
    }, [sortAnswers]);

    useEffect(() => {
      if (!useDescription) onChange({ ...option, description: null });
    }, [useDescription]);

    const handleImage = async (e) => {
      const image = await handleSetImage(e);

      if (image) onChange({ ...option, image });
    };

    const handleSetImage = async (e) => {
      return new Promise((resolve) => {
        const file = e.target.files[0];

        if (file) {
          const maxFileSize = 1;
          const fileSize = file.size / 1024 / 1024;
          const fileType = file.type;

          if (fileSize > maxFileSize) {
            alert({
              text: `Escolha um arquivo com no máximo ${maxFileSize}MB`,
            });
            return resolve(false);
          }

          const pattern = /image-*/;
          if (!fileType.match(pattern)) {
            alert({ text: "Arquivo inválido." });
            return resolve(false);
          }

          let reader = new FileReader();
          reader.onload = async (readerEvent) => {
            const imageSrc = readerEvent.target.result;
            const img = new Image();

            img.onload = async () => {
              if (img.width > 500 || img.height > 500) {
                alert({
                  text: "A imagem deve ter dimensões de até 500x500",
                });
                return resolve(false);
              }

              const response = await AssetsAPI.PostAssets({
                mime_type: fileType,
                file: imageSrc.split("base64,")[1],
              });

              const image = response.getData(null);
              return resolve(image);
            };

            img.src = imageSrc;
          };

          reader.readAsDataURL(file);
        }

        e.target.value = null;
      });
    };

    return (
      <div className="d-flex align-center">
        <div className={styles.optionItem}>
          <div
            className="d-flex align-center justify-between mb-20"
            onClick={() => setCollapsed(!collapsed)}
          >
            <Label className="mb-0">Resposta {optionIndex + 1}</Label>
            <If cond={!sortAnswers}>
              <div className={styles.chevron}>
                <i className="far fa-chevron-down" />
              </div>
            </If>
          </div>

          <div className="d-flex align-center">
            <label
              className={css({ picture: true, disabled: sortAnswers })}
              htmlFor={option.id}
            >
              <input
                type="file"
                id={option.id}
                accept="image/*"
                onChange={handleImage}
              />
              {option.image ? (
                <img
                  src={`${process.env.REACT_APP_ASSETS_URL}/${option.image}`}
                  alt=""
                />
              ) : (
                <>
                  <Icon name="image" light />
                  <span className={styles.plus}>
                    <Icon name="plus" size="xs" />
                  </span>
                </>
              )}
            </label>
            <Input
              design="two"
              className="w-100 ml-15"
              disabled={sortAnswers}
              defaultValue={option.name}
              onChange={(e) => {
                onChange({ ...option, name: e.target.value });
              }}
            />
            <If cond={!sortAnswers}>
              <Button
                design="default-icon"
                className="ml-15"
                onClick={() => onDelete(option, optionIndex)}
              >
                <Icon name="trash" light size="xl" />
              </Button>
            </If>
          </div>

          <If cond={collapsed}>
            <div className="mt-15">
              {useDescription && (
                <Textarea
                  design="three"
                  placeholder="Descrição"
                  value={option.description}
                  minHeight={100}
                  onChange={(e) => {
                    onChange({ ...option, description: e.target.value });
                  }}
                />
              )}

              <Input
                label="Código SKU"
                design="three"
                placeholder="----"
                className="mt-15"
                maxLength={60}
                suffix2={getTotalCharacters(option)}
                value={option.sku}
                onChange={(e) => onChange({ ...option, sku: e.target.value })}
              />

              <div className="d-flex align-center mt-25">
                {!option?.promo_price && (
                  <CurrencyInput
                    design="three"
                    value={option.price}
                    onChange={(e, m, f) => {
                      onChange({ ...option, price: f });
                    }}
                    placeholder="R$ 0,00"
                    className="w-100"
                    label="Preço"
                  />
                )}
                {!!option?.promo_price && (
                  <>
                    <CurrencyInput
                      design="three"
                      value={option.old_price || option.price}
                      onChange={(e, m, f) => {
                        onChange({ ...option, old_price: f });
                      }}
                      placeholder="R$ 0,00"
                      className="w-100"
                      label="Preço inicial"
                    />
                    <CurrencyInput
                      design="three"
                      value={option.price}
                      onChange={(e, m, f) => onChange({ ...option, price: f })}
                      placeholder="R$ 0,00"
                      className="w-100 ml-15"
                      label="Preço promocional"
                    />
                  </>
                )}
              </div>

              <Hairline className="mt-15 mb-15" />

              <Checkbox
                checked={useDescription}
                className="mb-10"
                design="two"
                onChange={() => setUseDescription(!useDescription)}
              >
                Utilizar descrição
              </Checkbox>
              <Checkbox
                checked={!!option.promo_price}
                design="two"
                onChange={(e) =>
                  onChange({
                    ...option,
                    promo_price: e,
                    old_price: !e ? null : option.price,
                    price: !e ? option.old_price : option.price,
                  })
                }
              >
                Utilizar promoção
              </Checkbox>
              <If cond={option.name}>
                <div className={styles.addStep}>
                  <p className={styles.qty}>ETAPAS ({option.items?.length})</p>
                  <If cond={!!option}>
                    <Button
                      onClick={() => handleOptionSteps(option)}
                      design="four"
                    >
                      <i className="fal fa-plus mr-10" /> Adicionar etapa
                    </Button>
                  </If>
                </div>
              </If>
            </div>
          </If>
        </div>

        <If cond={sortAnswers}>
          <div className={styles.sort}>
            <button
              className={css({ disabled: isLast })}
              onClick={() => moveDown(option, optionIndex)}
            >
              <i className="fal fa-chevron-down" />
            </button>

            <button
              className={css({ disabled: optionIndex == 0 })}
              onClick={() => moveUp(option, optionIndex)}
            >
              <i className="fal fa-chevron-up" />
            </button>
          </div>
        </If>
      </div>
    );
  };

  const Answers = ({
    question,
    onChange,
    onChangeQuestion,
    handleOptionSteps,
    onClose,
  }) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState(
      catalog.items[0]?.id || null
    );
    const [sortAnswers, setSortAnswers] = useState(false);
    const [isRequired, setIsRequired] = useState(true);

    const [answerType, setAnswerType] = useState(
      question.items.length > 0 ? "withText" : null
    );
    const [tempQuestion, setTempQuestion] = useState({
      ...question,
      price_method: question.price_method || "SUM",
      min: question.min ?? 1,
      max: question.max ?? 1,
    });

    const showFractionConfig = [1747, 2276].includes(+holdingId);

    const originalQuestionRef = useRef(null);

    useEffect(() => {
      if (question.min > 0) setIsRequired(true);
      else setIsRequired(false);
    }, [question]);

    return (
      <div className={styles.scrollContainer}>
        <div className={css({ question: true, notAllowed: sortAnswers })}>
          <Input
            label="Pergunta"
            design="two"
            placeholder="Ex. Deseja por molho?"
            defaultValue={tempQuestion.name}
            onChange={(e) =>
              setTempQuestion((question) => ({
                ...question,
                name: e.target.value,
              }))
            }
          />

          <Input
            label="Código SKU"
            design="two"
            placeholder="----"
            className="mt-15 mb-15"
            maxLength={60}
            suffix2={getTotalCharacters(tempQuestion)}
            value={tempQuestion.sku}
            onChange={(e) =>
              setTempQuestion((question) => ({
                ...question,
                sku: e.target.value,
              }))
            }
          />

          <div className={styles.minMax}>
            <Label>Obrigatoriedade das respostas</Label>
            <Hairline className="mt-10 mb-15" />
            <Radio
              family="required"
              className="mb-10"
              value={true}
              checked={isRequired}
              onChange={() => {
                setTempQuestion((question) => ({
                  ...question,
                  min: 1,
                  max: question.max >= 1 ? question.max : 1,
                }));
                setIsRequired(true);
              }}
            >
              Obrigatório
            </Radio>
            <Radio
              family="required"
              value={false}
              checked={isRequired}
              onChange={() => {
                setTempQuestion((question) => ({
                  ...question,
                  min: 0,
                  max: question.max >= 0 ? question.max : 0,
                }));
                setIsRequired(false);
              }}
            >
              Opcional
            </Radio>
            <div className={styles.spinners}>
              <div className="d-flex flex-column flex-1">
                <Label>Mínimo de opções</Label>
                <div className={styles.quantitySpinner}>
                  <div
                    className={css({
                      minus: true,
                      disabled: isRequired
                        ? tempQuestion.min === 1
                        : tempQuestion.min === 0,
                    })}
                    onClick={() =>
                      setTempQuestion((question) => ({
                        ...question,
                        min: question.min - 1,
                      }))
                    }
                  >
                    <i className="far fa-minus" />
                  </div>

                  <p className={styles.quantity}>{tempQuestion.min}</p>

                  <div
                    className={styles.plus}
                    onClick={() =>
                      setTempQuestion((question) => ({
                        ...question,
                        min: question.min + 1,
                        max: question.min + 1,
                      }))
                    }
                  >
                    <i className="far fa-plus" />
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column flex-1">
                <Label>Máximo de opções</Label>
                <div className={styles.quantitySpinner}>
                  <div
                    className={css({
                      minus: true,
                      disabled:
                        (isRequired
                          ? tempQuestion.max === 1
                          : tempQuestion.max === 0) ||
                        tempQuestion.max <= tempQuestion.min,
                    })}
                    onClick={() =>
                      setTempQuestion((question) => ({
                        ...question,
                        max: question.max - 1,
                      }))
                    }
                  >
                    <i className="far fa-minus" />
                  </div>

                  <p className={styles.quantity}>{tempQuestion.max}</p>

                  <div
                    className={styles.plus}
                    onClick={() =>
                      setTempQuestion((question) => ({
                        ...question,
                        max: question.max + 1,
                      }))
                    }
                  >
                    <i className="far fa-plus" />
                  </div>
                </div>
              </div>
            </div>

            <Hairline className="mt-15 mb-15" />

            <Toggle2
              title="Permitir várias seleções de um mesmo item"
              checked={tempQuestion.mode == "quantity"}
              reverse
              onChange={() =>
                setTempQuestion((question) => ({
                  ...question,
                  mode: question.mode === "quantity" ? "option" : "quantity",
                }))
              }
            />
          </div>

          {showFractionConfig && (
            <div class="mb-20">
              <div className={styles.minMax}>
                <Checkbox
                  name={`quantity_type_${Date.now()}`}
                  design="two"
                  checked={tempQuestion.quantityType === "fraction"}
                  data-tooltip="Cliques"
                  onChange={(value) =>
                    setTempQuestion((question) => ({
                      ...question,
                      quantityType: value ? "fraction" : "default",
                    }))
                  }
                  tooltip="Configuração interna para indicar que os itens da etapa são frações de um produto. Utilizado na integração com o Atena.ai"
                >
                  Fracionado
                </Checkbox>
              </div>
            </div>
          )}

          <div className={styles.minMax}>
            <Label className="mb-10">Cálculo de preço</Label>
            <div class="d-flex align-center">
              <Select
                design="one"
                className="flex-1"
                value={tempQuestion.price_method}
                options={[
                  { value: "SUM", label: "Soma das etapas" },
                  { value: "MAX", label: "Máximo das etapas" },
                ]}
                onChange={(e) => {
                  setTempQuestion((question) => ({
                    ...question,
                    price_method: e.target.value,
                  }));
                }}
              />
            </div>
          </div>
        </div>

        <Hairline className="mt-20 mb-20" />

        <div className={styles.items}>
          <div className="d-flex align-center justify-between mb-20">
            <Label className="mb-0">Respostas</Label>
            <If cond={tempQuestion.items.length}>
              <span className={styles.qty}>({tempQuestion.items.length})</span>
            </If>
          </div>

          {tempQuestion.items.map((option, optionIndex) => (
            <OptionItem
              option={option}
              question={question}
              key={`option-${option.ref_id}`}
              optionIndex={optionIndex}
              onChange={(option) => {
                tempQuestion.items[optionIndex] = option;

                setTempQuestion((question) => ({
                  ...question,
                  ...tempQuestion,
                }));
              }}
              onDelete={(option, oIndex) => {
                let tempItems = tempQuestion.items;
                tempItems = tempQuestion.items.filter((o, oIdx) => {
                  return oIdx !== oIndex;
                });

                setTempQuestion((question) => ({
                  ...question,
                  items: tempItems,
                }));
                if (tempQuestion.items.length == 0) {
                  setAnswerType(null);
                }
              }}
              handleOptionSteps={(option) => handleOptionSteps(option)}
              sortAnswers={sortAnswers}
              moveUp={(option, oIndex) => {
                let tempItems = tempQuestion.items;
                let item = tempItems[oIndex];
                tempItems[oIndex] = tempItems[oIndex - 1];
                tempItems[oIndex - 1] = item;
                setTempQuestion((question) => ({
                  ...question,
                  items: tempItems.map((item, index) => ({
                    ...item,
                    position: index,
                  })),
                }));
              }}
              moveDown={(option, oIndex) => {
                let tempItems = tempQuestion.items;
                let item = tempItems[oIndex];
                tempItems[oIndex] = tempItems[oIndex + 1];
                tempItems[oIndex + 1] = item;
                setTempQuestion((question) => ({
                  ...question,
                  items: tempItems.map((item, index) => ({
                    ...item,
                    position: index,
                  })),
                }));
              }}
              isLast={optionIndex == tempQuestion.items.length - 1}
            />
          ))}
          <If cond={!sortAnswers}>
            <Add2
              className="w-100 mt-10"
              onClick={() => {
                tempQuestion.items.push({
                  ref_id: uuid.v4(),
                  type: "option",
                  items: [],
                  name: "",
                  price: 0,
                });
                setTempQuestion((question) => ({
                  ...question,
                  ...tempQuestion,
                }));
              }}
              text="Criar nova resposta"
            />
          </If>
        </div>

        <If cond={!sortAnswers}>
          <div className={styles.footerColumn}>
            <Button
              design="primary"
              className="w-100"
              onClick={() => {
                onChangeQuestion(tempQuestion);
                onClose();
              }}
              disabled={
                tempQuestion.items.find((q) => !q.name) ||
                !tempQuestion.name ||
                tempQuestion.items.length === 0
              }
            >
              Salvar etapa
            </Button>
            <If cond={tempQuestion.items.length > 1}>
              <Button
                design="primary-outline"
                className="w-100 mt-15"
                onClick={() => {
                  setSortAnswers(true);
                  originalQuestionRef.current = JSON.parse(
                    JSON.stringify(tempQuestion)
                  );
                }}
              >
                Ordenar respostas
              </Button>
            </If>
          </div>
        </If>
        <If cond={sortAnswers}>
          <div className={styles.footer}>
            <Button
              design="primary"
              className="w-100"
              onClick={() => {
                onChangeQuestion(tempQuestion);
                setSortAnswers(false);
              }}
              disabled={
                tempQuestion.items.find((q) => !q.name) ||
                !tempQuestion.name ||
                tempQuestion.items.length === 0
              }
            >
              Salvar etapa
            </Button>
            <Button
              className="w-100 ml-15"
              onClick={() => {
                setSortAnswers(false);
                setTempQuestion(originalQuestionRef.current);
              }}
            >
              Cancelar
            </Button>
          </div>
        </If>
      </div>
    );
  };

  return (
    <>
      <Drawer
        visible={open}
        onClose={() => {
          if (!locked) setOpen(false);
        }}
        title={
          currentStep === "NEW_STEP" ? "Cadastrar etapa" : "Banco de etapas"
        }
        showBackButton={!locked}
        showCloseButton={!locked}
        withoutTransition
        backButton={() => {
          if (currentStep !== "NEW_STEP") setOpen(false);
          else setCurrentStep(null);
        }}
      >
        <If cond={locked}>
          <div className={styles.updating}>
            <p>O cardápio está atualizando</p>
            <Loading />
          </div>
        </If>

        <If cond={loading}>
          <div className={styles.updating}>
            <Loading />
          </div>
        </If>

        <If cond={(!steps || steps.length === 0) && currentStep !== "NEW_STEP"}>
          <div className={styles.empty}>
            <Icon name="folder-tree" size="xl" />
            <p className={styles.title}>
              Você ainda não possui etapas criadas no banco de etapas
            </p>
            <Add
              text="Cadastrar etapa"
              className="d-flex flex-column"
              onClick={() => {
                setCurrentStep("NEW_STEP");
                setSelectedQuestion(getEmptyQuestion());
              }}
            />
          </div>
        </If>

        <If cond={steps.length > 0 && currentStep !== "NEW_STEP"}>
          <div className={styles.steps}>
            {steps.map((step, index) => (
              <div className={styles.step} key={index}>
                <div className={styles.header}>
                  <div className="d-flex flex-column">
                    <p className={styles.name}>{step.name}</p>
                    <span className={styles.quantity}>
                      {step.items.length} respostas
                    </span>
                  </div>

                  {!locked && (
                    <Button
                      design="default-icon"
                      onClick={() => {
                        setCurrentStep("NEW_STEP");
                        setSelectedQuestion(step);
                      }}
                    >
                      <Icon name="pen" size="sm" />
                    </Button>
                  )}
                </div>
                <div className={styles.tags}>
                  <div className={styles.tag}>
                    {step.min > 0 ? "Etapa obrigatória" : "Etapa opcional"}
                  </div>
                  <div className={styles.tag}>Mín. de opções: {step.min}</div>
                  <div className={styles.tag}>Máx. de opções: {step.max}</div>
                </div>
              </div>
            ))}
          </div>

          <Add
            text="Cadastrar etapa"
            className="d-flex flex-column mt-15"
            onClick={() => {
              setCurrentStep("NEW_STEP");
              setSelectedQuestion(getEmptyQuestion());
            }}
          />
        </If>

        <If cond={currentStep === "NEW_STEP"}>
          <Answers
            question={selectedQuestion || getEmptyQuestion(true)}
            onChange={(data) => console.log("onChange", data)}
            onChangeQuestion={async (question) => {
              console.log("[OnSaveStep] Salvando a etapa...", question);

              setSteps((prev) => {
                const index = prev.findIndex(
                  (s) => s.ref_id === question.ref_id
                );

                if (index !== -1)
                  return prev.map((s, i) => (i === index ? question : s));
                return [...prev, question];
              });

              await upsertStep(question);

              setCurrentStep(null);
            }}
            handleOptionSteps={(data) => {
              setOpenOptionSteps(true);
              setSelectedOption(data);
            }}
            onClose={() => {}}
          />
        </If>
      </Drawer>

      <Drawer
        visible={openOptionSteps}
        onClose={() => setOpenOptionSteps(false)}
        withoutTransition
        closeInFade={false}
        showBackButton
        title="Cadastrar etapa"
        subtitle={selectedOption.name}
        backButton={() => setOpenOptionSteps(false)}
      >
        <OptionStepDatabaseQuestion
          option={selectedOption}
          onChange={(option) => {
            console.log(option);
            onChangeOption(option);
          }}
          onDeleteQuestion={(idx) => onDeleteQuestion(idx)}
          handleOptionSteps={(option) => {
            console.log("option", option);
            setOpenOptionSteps(true);
            setSelectedOption(option);
          }}
        />

        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            onClick={() => setOpenOptionSteps(false)}
          >
            Salvar etapa
          </Button>
        </div>
      </Drawer>

      <Drawer
        visible={openFinished}
        withoutTransition
        onClose={() => {
          setOpenFinished(false);
        }}
        title={`Etapa cadastrada com sucesso`}
        subtitle="Você já pode encontrar esta etapa cadastrada no banco de etapas."
      >
        <div className={styles.successIcon}>
          <SuccessIcon />
        </div>
        <div className={styles.footer}>
          <Button
            design="default-3"
            className="w-100"
            onClick={() => {
              setOpenFinished(false);
            }}
          >
            Ver banco de etapas
          </Button>
        </div>
      </Drawer>
    </>
  );
}
